import React, { useState, useEffect, useCallback } from 'react';
import { host } from '../script/variables';
import '../style/expense.css';

const FIXED_CATEGORIES   = ['Rent', 'Staff Salary', 'Subscriptions', 'Insurance', 'Others'];
const DYNAMIC_CATEGORIES = ['Electricity', 'Inventory Cost', 'Gas', 'Supplies', 'Maintenance', 'Others'];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const TYPE_META = {
  fixed: {
    emoji: '🔒',
    badge: 'Fixed · Auto-repeats monthly',
    title: 'Fixed Expenses',
    oneliner: 'Rent, salary — same every month',
    modalTitle: 'Same amount, every month',
    context: 'Auto-repeats each month until you remove or update it. Best for Rent, Staff Salary, Insurance.',
    categories: FIXED_CATEGORIES,
  },
  dynamic: {
    emoji: '📊',
    badge: 'Dynamic · Enter each month',
    title: 'Dynamic Expenses',
    oneliner: 'Electricity, inventory — varies, enter actual bill',
    modalTitle: 'Happens monthly, amount varies',
    context: 'Happens every month but the bill changes. Enter the actual amount when you receive it.',
    categories: DYNAMIC_CATEGORIES,
  },
};

const getCurrentMonth = () => new Date().getMonth() + 1;
const getCurrentYear  = () => new Date().getFullYear();
const todayIso        = () => new Date().toISOString().slice(0, 10);

const formatMoney = (n) =>
  '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatPeriod = (item) =>
  item.periodMonth >= 1 && item.periodMonth <= 12 && item.periodYear
    ? `${MONTHS[item.periodMonth - 1].slice(0, 3)} ${item.periodYear}`
    : '—';

const formatDateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
};

const yearOptions = () => {
  const y = getCurrentYear();
  return Array.from({ length: 4 }, (_, i) => y - 2 + i);
};

/* ─── Delete Confirm inline ─── */
const DeleteConfirm = ({ onConfirm, onCancel }) => (
  <span className="exp-del-confirm">
    <span className="exp-del-confirm__text">Remove?</span>
    <button type="button" className="btn-sm btn-sm--danger" onClick={onConfirm}>Yes</button>
    <button type="button" className="btn-sm" onClick={onCancel}>No</button>
  </span>
);

/* ─── Expense Row ─── */
const ExpenseRow = ({ item, onEdit, onDelete }) => {
  const [confirm, setConfirm] = useState(false);
  return (
    <li className="exp-item">
      <span className="exp-item__name">{item.name}</span>
      <span className="exp-item__period">{formatPeriod(item)}</span>
      <span className="exp-item__amt">{formatMoney(item.amount)}</span>
      <div className="exp-item__actions">
        {confirm ? (
          <DeleteConfirm onConfirm={() => { onDelete(item._id); setConfirm(false); }} onCancel={() => setConfirm(false)} />
        ) : (
          <>
            <button type="button" className="btn-sm" onClick={() => onEdit(item)}>Edit</button>
            <button type="button" className="btn-sm btn-sm--del" onClick={() => setConfirm(true)}>Delete</button>
          </>
        )}
      </div>
    </li>
  );
};

/* ══════════════════════════════════════
   ROOT
══════════════════════════════════════ */
const Expense = ({ cafeid }) => {
  const [data, setData] = useState({
    fixed: [], dynamic: [], daily: [],
    totalFixed: 0, totalDynamic: 0, totalDaily: 0, grandTotal: 0
  });
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [filterYear,    setFilterYear]    = useState(String(getCurrentYear()));
  const [filterMonth,   setFilterMonth]   = useState(String(getCurrentMonth()));
  const [explainerOpen, setExplainerOpen] = useState(false);

  const [form, setForm] = useState({
    type: 'fixed', name: FIXED_CATEGORIES[0], amount: '',
    periodMonth: getCurrentMonth(), periodYear: getCurrentYear(), notes: ''
  });

  const [dailyForm,      setDailyForm]      = useState({ date: todayIso(), note: '', amount: '' });
  const [dailyEditingId, setDailyEditingId] = useState(null);

  const getToken = () => localStorage.getItem('partnertoken');

  /* ── fetch ── */
  const fetchExpenses = useCallback(async () => {
    if (!cafeid) return;
    setLoading(true); setError('');
    try {
      let url = `${host}/api/expense/${cafeid}`;
      if (filterYear) {
        url += `?year=${filterYear}`;
        if (filterMonth) url += `&month=${filterMonth}`;
      }
      const res  = await fetch(url, { headers: { Authorization: getToken() } });
      if (!res.ok) throw new Error((await res.json()).msg || 'Failed to fetch');
      const json = await res.json();
      setData({
        fixed: [], dynamic: [], daily: [],
        totalFixed: 0, totalDynamic: 0, totalDaily: 0, grandTotal: 0,
        ...(json.data || {})
      });
    } catch (e) {
      setError(e.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [cafeid, filterYear, filterMonth]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(t); }
  }, [success]);

  /* sync daily date to viewed period */
  useEffect(() => {
    if (!filterYear || !filterMonth) return;
    const y = parseInt(filterYear, 10); const m = parseInt(filterMonth, 10);
    if (!y || !m) return;
    const now = new Date();
    const isCurrent = now.getFullYear() === y && now.getMonth() + 1 === m;
    setDailyForm(p => ({ ...p, date: isCurrent ? todayIso() : `${y}-${String(m).padStart(2,'0')}-01` }));
  }, [filterYear, filterMonth]);

  /* ── modal helpers ── */
  const openAdd = (type) => {
    setEditingId(null);
    setForm({ type, name: TYPE_META[type].categories[0], amount: '', periodMonth: getCurrentMonth(), periodYear: getCurrentYear(), notes: '' });
    setShowModal(true);
  };
  const openEdit = (item) => {
    setEditingId(item._id);
    setForm({
      type: item.type,
      name: item.name || TYPE_META[item.type].categories[0],
      amount: item.amount ?? '',
      periodMonth: item.periodMonth >= 1 && item.periodMonth <= 12 ? item.periodMonth : getCurrentMonth(),
      periodYear:  item.periodYear || getCurrentYear(),
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const listForType     = form.type === 'fixed' ? data.fixed : data.dynamic;
  const duplicateInList = !editingId && listForType.some(
    i => i.name === form.name && i.periodMonth === form.periodMonth && i.periodYear === form.periodYear
  );

  /* ── submit fixed/dynamic ── */
  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (duplicateInList) { setError('This category already exists for this month.'); return; }
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount < 0) { setError('Enter a valid amount'); return; }
    try {
      const body = { cafeId: cafeid, type: form.type, name: form.name, amount, periodMonth: form.periodMonth, periodYear: form.periodYear, notes: form.notes.trim() };
      const url  = editingId ? `${host}/api/expense/${editingId}` : `${host}/api/expense`;
      const res  = await fetch(url, { method: editingId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: getToken() }, body: JSON.stringify(body) });
      const j    = await res.json();
      if (!res.ok) throw new Error(j.msg || (editingId ? 'Update failed' : 'Add failed'));
      setSuccess(editingId ? 'Expense updated' : 'Expense added');
      setShowModal(false); fetchExpenses();
    } catch (e) { setError(e.message || 'Something went wrong'); }
  };

  /* ── daily submit ── */
  const handleAddDaily = async (e) => {
    e.preventDefault(); setError('');
    const amt = parseFloat(dailyForm.amount);
    if (!dailyForm.note.trim()) { setError('Enter a note'); return; }
    if (isNaN(amt) || amt < 0)  { setError('Enter a valid amount'); return; }
    try {
      const payload = { cafeId: cafeid, date: dailyForm.date, note: dailyForm.note.trim(), amount: amt };
      const url     = dailyEditingId ? `${host}/api/expense/daily/${dailyEditingId}` : `${host}/api/expense/daily`;
      const res     = await fetch(url, { method: dailyEditingId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: getToken() }, body: JSON.stringify(payload) });
      const j       = await res.json();
      if (!res.ok) throw new Error(j.msg || 'Failed to save');
      setSuccess(dailyEditingId ? 'Updated' : 'Logged!');
      setDailyEditingId(null);
      setDailyForm(p => ({ ...p, note: '', amount: '' }));
      fetchExpenses();
    } catch (e) { setError(e.message || 'Could not save'); }
  };

  /* ── delete fixed/dynamic ── */
  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${host}/api/expense/${id}`, { method: 'DELETE', headers: { Authorization: getToken() } });
      if (!res.ok) throw new Error((await res.json()).msg || 'Delete failed');
      setSuccess('Removed'); fetchExpenses();
    } catch (e) { setError(e.message || 'Could not remove'); }
  };

  /* ── delete daily ── */
  const handleDeleteDaily = async (id) => {
    try {
      const res = await fetch(`${host}/api/expense/daily/${id}`, { method: 'DELETE', headers: { Authorization: getToken() } });
      if (!res.ok) throw new Error((await res.json()).msg || 'Delete failed');
      setSuccess('Removed');
      if (dailyEditingId === id) { setDailyEditingId(null); setDailyForm(p => ({ ...p, note: '', amount: '' })); }
      fetchExpenses();
    } catch (e) { setError(e.message || 'Could not remove'); }
  };

  /* ── group daily by date ── */
  const groupedDaily = () => {
    const byDate = {};
    (data.daily || []).forEach(item => {
      if (!item.date) return;
      const k = item.date.slice(0, 10);
      if (!byDate[k]) byDate[k] = [];
      byDate[k].push(item);
    });
    return Object.entries(byDate).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  };

  /* ── stats percentages ── */
  const grand = data.grandTotal || 1; // avoid /0
  const pct   = (n) => Math.round((n / grand) * 100);

  if (loading) return (
    <div className="expense-page">
      <div className="expense-loading">
        <div className="expense-spinner" />
        <p>Loading expenses…</p>
      </div>
    </div>
  );

  return (
    <div className="expense-page">

      {/* ── Page header + period filter ── */}
      <div className="exp-page-head">
        <div className="exp-page-head__left">
          <h1 className="exp-page-title">Expenses</h1>
          <p className="exp-page-sub">Log and track every rupee your restaurant spends</p>
        </div>
        <div className="exp-period">
          <label className="exp-period__label">View</label>
          <select
            value={filterYear}
            onChange={e => { setFilterYear(e.target.value); if (!e.target.value) setFilterMonth(''); }}
            className="exp-select"
          >
            <option value="">All time</option>
            {yearOptions().map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {filterYear && (
            <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="exp-select">
              <option value="">All months</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* ── Toast messages ── */}
      {success && <div className="exp-toast exp-toast--success">{success}</div>}
      {error   && <div className="exp-toast exp-toast--error">{error} <button onClick={() => setError('')}>✕</button></div>}

      {/* ══ STATS — 4 cards with proportion bars ══ */}
      <div className="exp-stats">
        {[
          { cls: 'fixed',  icon: '🔒', label: 'Fixed',      val: data.totalFixed,   hint: 'Rent, salary…',        p: pct(data.totalFixed) },
          { cls: 'dyn',    icon: '📊', label: 'Dynamic',    val: data.totalDynamic, hint: 'Electricity, inventory…', p: pct(data.totalDynamic) },
          { cls: 'daily',  icon: '📝', label: 'Daily',      val: data.totalDaily,   hint: 'One-off spends',        p: pct(data.totalDaily) },
          { cls: 'total',  icon: '💰', label: 'Grand Total', val: data.grandTotal,  hint: null,                    p: null },
        ].map(s => (
          <div key={s.cls} className={`exp-stat exp-stat--${s.cls}`}>
            <div className="exp-stat__top">
              <span className="exp-stat__icon">{s.icon}</span>
              {s.p !== null && <span className="exp-stat__pct">{s.p}%</span>}
            </div>
            <div className="exp-stat__amount">{formatMoney(s.val)}</div>
            <div className="exp-stat__label">{s.label}</div>
            {s.hint && <div className="exp-stat__hint">{s.hint}</div>}
            {s.p !== null && (
              <div className="exp-stat__bar">
                <div className="exp-stat__bar-fill" style={{ width: `${s.p}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>

           {/* ══ TYPE EXPLAINER — collapsed, expandable ══ */}
      <div className="exp-explainer">
        <button
          type="button"
          className="exp-explainer__toggle"
          onClick={() => setExplainerOpen(o => !o)}
        >
          <span>💡 What's the difference between Fixed, Dynamic and Daily?</span>
          <span className={`exp-explainer__chevron ${explainerOpen ? 'open' : ''}`}>▼</span>
        </button>
        {explainerOpen && (
          <div className="exp-explainer__body">
            {[
              { cls: 'fixed', icon: '🔒', name: 'Fixed', title: 'Same amount, every month', desc: 'Set it once — auto-repeats monthly. Only update if the amount changes (e.g. salary revision).', chips: ['🏠 Rent', '👥 Staff Salary', '📋 Insurance'] },
              { cls: 'dyn',   icon: '📊', name: 'Dynamic', title: 'Happens monthly, bill varies', desc: 'Enter the actual bill each month when it arrives. Starts at ₹0 every month.', chips: ['⚡ Electricity', '🛒 Inventory Cost', '🔥 Gas'] },
              { cls: 'daily', icon: '📝', name: 'Daily', title: 'Random one-off spends', desc: 'Log as it happens. No category, no monthly tracking — just note it and the amount.', chips: ['🧹 Minor Repair', '🎉 Festival Decor', '🚗 Delivery'] },
            ].map(t => (
              <div key={t.cls} className={`exp-type-card exp-type-card--${t.cls}`}>
                <div className="exp-type-card__icon">{t.icon}</div>
                <div className="exp-type-card__name">{t.name}</div>
                <div className="exp-type-card__title">{t.title}</div>
                <div className="exp-type-card__desc">{t.desc}</div>
                <div className="exp-type-card__chips">
                  {t.chips.map(c => <span key={c} className="exp-chip">{c}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══ DAILY QUICK-LOG — always visible, first action ══ */}
      <div className="exp-daily-card">
        <div className="exp-daily-card__head">
          <div className="exp-daily-card__head-left">
            <div className="exp-daily-card__icon">📝</div>
            <div>
              <div className="exp-daily-card__badge">Daily · Log as it happens</div>
              <div className="exp-daily-card__title">Quick Log</div>
              <div className="exp-daily-card__hint">Small one-off spends — note it now, done</div>
            </div>
          </div>
          <span className="exp-daily-card__total">{formatMoney(data.totalDaily)} this period</span>
        </div>

        {/* Inline form — no modal, no scroll */}
        <form className="exp-daily-form" onSubmit={handleAddDaily}>
          <div className="exp-daily-form__row">
            <div className="exp-daily-field exp-daily-field--date">
              <label>Date</label>
              <input
                type="date"
                value={dailyForm.date}
                onChange={e => setDailyForm(p => ({ ...p, date: e.target.value }))}
              />
            </div>
            <div className="exp-daily-field exp-daily-field--note">
              <label>What was this for?</label>
              <input
                type="text"
                value={dailyForm.note}
                onChange={e => setDailyForm(p => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Minor Repair, Festival Decor…"
                autoComplete="off"
              />
            </div>
            <div className="exp-daily-field exp-daily-field--amt">
              <label>Amount (₹)</label>
              <input
                type="number" min="0" step="0.01"
                value={dailyForm.amount}
                onChange={e => setDailyForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="exp-daily-field">
              <label>&nbsp;</label>
              <button type="submit" className="btn-log">
                {dailyEditingId ? 'Update' : '+ Log it'}
              </button>
            </div>
          </div>
          {dailyEditingId && (
            <div className="exp-daily-edit-hint">
              Editing existing entry.{' '}
              <button type="button" onClick={() => { setDailyEditingId(null); setDailyForm(p => ({ ...p, note: '', amount: '' })); }}>
                Cancel
              </button>
            </div>
          )}
        </form>

        {/* Daily entries */}
        <div className="exp-daily-list">
          {groupedDaily().length === 0 ? (
            <p className="exp-empty">No daily expenses recorded for this period.</p>
          ) : (
            groupedDaily().map(([dateKey, items]) => {
              const daySum = items.reduce((s, i) => s + i.amount, 0);
              return (
                <div key={dateKey} className="exp-daily-group">
                  <div className="exp-daily-group__date-row">
                    <span className="exp-daily-group__pill">{formatDateLabel(dateKey)}</span>
                    <div className="exp-daily-group__rule" />
                    <span className="exp-daily-group__sum">{formatMoney(daySum)}</span>
                  </div>
                  {items.map(item => (
                    <div key={item._id} className="exp-daily-item">
                      <span className="exp-daily-item__note">{item.note}</span>
                      <span className="exp-daily-item__amt">{formatMoney(item.amount)}</span>
                      <div className="exp-daily-item__actions">
                        <button
                          type="button" className="btn-sm"
                          onClick={() => {
                            setDailyEditingId(item._id);
                            setDailyForm({ date: (item.date || '').slice(0, 10), note: item.note || '', amount: item.amount != null ? String(item.amount) : '' });
                          }}
                        >Edit</button>
                        <button type="button" className="btn-sm btn-sm--del" onClick={() => handleDeleteDaily(item._id)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>

 

      {/* ══ FIXED SECTION ══ */}
      {['dynamic', 'fixed'].map(type => {
        const m    = TYPE_META[type];
        const list = type === 'fixed' ? data.fixed : data.dynamic;
        const tot  = type === 'fixed' ? data.totalFixed : data.totalDynamic;
        return (
          <section key={type} className={`exp-section exp-section--${type === 'dynamic' ? 'dyn' : type}`}>
            <div className="exp-section__head">
              <div className="exp-section__head-left">
                <div className="exp-section__icon">{m.emoji}</div>
                <div className="exp-section__name-block">
                  <span className="exp-section__badge">{m.badge}</span>
                  <h2 className="exp-section__title">{m.title}</h2>
                  <span className="exp-section__oneliner">{m.oneliner}</span>
                </div>
              </div>
              <div className="exp-section__right">
                <span className="exp-section__total">{formatMoney(tot)}</span>
                <button type="button" className="btn-add" onClick={() => openAdd(type)}>+ Add</button>
              </div>
            </div>
            {list.length === 0 ? (
              <p className="exp-empty">No {type} expenses for this period.</p>
            ) : (
              <ul className="exp-list">
                {list.map(item => (
                  <ExpenseRow key={item._id} item={item} onEdit={openEdit} onDelete={handleDelete} />
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {/* ══ ADD / EDIT MODAL ══ */}
      {showModal && (
        <div className="exp-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="exp-modal" onClick={e => e.stopPropagation()}>
            <div className="exp-modal__header">
              <h3>{editingId ? 'Edit expense' : 'Add expense'}</h3>
              <button type="button" className="exp-modal__close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="exp-form">

              {/* Type picker — only shown when adding */}
              {!editingId && (
                <div className="exp-form__row">
                  <label>What kind of expense is this?</label>
                  <div className="exp-type-picker">
                    {['fixed', 'dynamic'].map(t => (
                      <div
                        key={t}
                        className={`exp-type-option exp-type-option--${t === 'dynamic' ? 'dyn' : t} ${form.type === t ? 'selected' : ''}`}
                        onClick={() => setForm(f => ({ ...f, type: t, name: TYPE_META[t].categories[0] }))}
                        role="button"
                      >
                        <span className="exp-type-option__emoji">{TYPE_META[t].emoji}</span>
                        <span className="exp-type-option__name">{t.charAt(0).toUpperCase() + t.slice(1)}</span>
                        <span className="exp-type-option__hint">{TYPE_META[t].modalTitle}</span>
                        <span className={`exp-type-option__dot ${form.type === t ? 'active' : ''}`} />
                      </div>
                    ))}
                  </div>
                  {/* Context banner */}
                  <div className={`exp-type-ctx exp-type-ctx--${form.type === 'dynamic' ? 'dyn' : form.type}`}>
                    <span>💡</span>
                    <span>{TYPE_META[form.type]?.context}</span>
                  </div>
                </div>
              )}

              <div className="exp-form__row">
                <label>Category *</label>
                <select
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required className="exp-form__input"
                >
                  {TYPE_META[form.type].categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="exp-form__row">
                <label>Amount (₹) *</label>
                <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" required className="exp-form__input" />
              </div>

              <div className="exp-form__row exp-form__row--inline">
                <div>
                  <label>Month</label>
                  <select value={form.periodMonth} onChange={e => setForm(f => ({ ...f, periodMonth: parseInt(e.target.value, 10) }))} className="exp-form__input">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label>Year</label>
                  <select value={form.periodYear} onChange={e => setForm(f => ({ ...f, periodYear: parseInt(e.target.value, 10) }))} className="exp-form__input">
                    {yearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {duplicateInList && <p className="exp-form__warning">This category already exists for this month. Edit the existing entry instead.</p>}

              <div className="exp-form__row">
                <label>Notes (optional)</label>
                <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any details" className="exp-form__input" />
              </div>

              <div className="exp-modal__footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={duplicateInList}>{editingId ? 'Update' : 'Add expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expense;